import React from "react";
import { createRoot, type Root } from "react-dom/client";
// Import CSS for widget build - ensure it's loaded
import "../index.css";

// Import and re-export config type and WidgetContent from shared component
import {
  type CuadraUIKitConfig,
  WidgetContent,
} from "../components/WidgetContent";
export type { CuadraUIKitConfig };
export { WidgetContent };

class CuadraUIKitWidget {
  private config: CuadraUIKitConfig | null = null;
  private root: Root | null = null;
  private container: HTMLElement | null = null;
  private instances: Map<string, CuadraUIKitWidget> = new Map();

  init(config: CuadraUIKitConfig): void {
    // Validate required fields
    if (!config.baseUrl && !config.proxyUrl) {
      throw new Error("Either baseUrl or proxyUrl is required");
    }

    // modelId is optional - backend can resolve it in proxy mode
    // Only validate if explicitly provided but empty
    if (
      config.modelMode === "fixed" &&
      config.modelId !== undefined &&
      (!config.modelId || config.modelId.trim() === "")
    ) {
      throw new Error(
        "modelId cannot be empty when explicitly provided. Omit modelId to let backend resolve it.",
      );
    }

    // Find or create container
    const containerId = config.containerId || "cuadra-chat";
    let container = document.getElementById(containerId);

    if (!container) {
      container = document.createElement("div");
      container.id = containerId;
      document.body.appendChild(container);
    }

    // Add container class if provided
    if (config.containerClass) {
      container.classList.add(...config.containerClass.split(" "));
    }

    // Store config
    this.config = { ...config, containerId };
    this.container = container;

    // Mount React app
    this.root = createRoot(container);
    this.root.render(
      React.createElement(WidgetWrapper, { config: this.config }),
    );

    // Store instance
    this.instances.set(containerId, this);
  }

  destroy(containerId?: string): void {
    const targetId = containerId || this.config?.containerId;
    if (!targetId) return;

    const instance = this.instances.get(targetId);
    if (instance?.root) {
      instance.root.unmount();
      instance.root = null;
    }

    if (instance?.container && instance.container.parentNode) {
      instance.container.parentNode.removeChild(instance.container);
    }

    this.instances.delete(targetId);
  }

  updateConfig(
    updates: Partial<CuadraUIKitConfig>,
    containerId?: string,
  ): void {
    const targetId = containerId || this.config?.containerId;
    if (!targetId) return;

    const instance = this.instances.get(targetId);
    if (!instance?.config) return;

    // Merge updates
    const newConfig = { ...instance.config, ...updates };
    instance.config = newConfig;

    // Re-render with new config
    if (instance.root && instance.container) {
      instance.root.render(
        React.createElement(WidgetWrapper, { config: newConfig }),
      );
    }
  }

  getInstance(containerId?: string): CuadraUIKitWidget | null {
    const targetId = containerId || this.config?.containerId;
    if (!targetId) return null;
    return this.instances.get(targetId) || null;
  }

  // Parse config from data attributes
  static parseConfigFromAttributes(
    script: HTMLScriptElement,
    container?: HTMLElement,
  ): Partial<CuadraUIKitConfig> {
    const config: Partial<CuadraUIKitConfig> = {};

    // Parse script tag data attributes
    if (script.dataset.baseUrl) config.baseUrl = script.dataset.baseUrl;
    if (script.dataset.proxyUrl) config.proxyUrl = script.dataset.proxyUrl;
    if (script.dataset.sessionToken)
      config.sessionToken = script.dataset.sessionToken;
    if (script.dataset.containerId)
      config.containerId = script.dataset.containerId;
    if (script.dataset.containerClass)
      config.containerClass = script.dataset.containerClass;
    if (script.dataset.mode)
      config.mode = script.dataset.mode as "singleChat" | "multiChat";
    if (script.dataset.modelMode)
      config.modelMode = script.dataset.modelMode as "selector" | "fixed";
    if (script.dataset.modelId) config.modelId = script.dataset.modelId;
    if (script.dataset.ephemeral)
      config.ephemeral = script.dataset.ephemeral === "true";
    if (script.dataset.systemPrompt)
      config.systemPrompt = script.dataset.systemPrompt;

    // Parse container data attributes (override script tag)
    if (container) {
      if (container.dataset.baseUrl) config.baseUrl = container.dataset.baseUrl;
      if (container.dataset.proxyUrl)
        config.proxyUrl = container.dataset.proxyUrl;
      if (container.dataset.sessionToken)
        config.sessionToken = container.dataset.sessionToken;
      if (container.dataset.mode)
        config.mode = container.dataset.mode as "singleChat" | "multiChat";
      if (container.dataset.modelMode)
        config.modelMode = container.dataset.modelMode as "selector" | "fixed";
      if (container.dataset.modelId) config.modelId = container.dataset.modelId;
    }

    return config;
  }
}

interface WidgetWrapperProps {
  config: CuadraUIKitConfig;
}

/**
 * Internal wrapper component for widget usage
 * WidgetContent is now imported from shared component file
 */
const WidgetWrapper: React.FC<WidgetWrapperProps> = ({ config }) => {
  return <WidgetContent config={config} />;
};

// Create global instance
let globalWidget: CuadraUIKitWidget | null = null;

// Initialize global widget instance
if (typeof window !== "undefined") {
  globalWidget = new CuadraUIKitWidget();
}

// Create the API object that will be exposed
const CuadraUIKitAPI = {
  /**
   * Initialize the widget with configuration
   * @param config Widget configuration
   */
  init: (config: CuadraUIKitConfig) => {
    if (!globalWidget) {
      globalWidget = new CuadraUIKitWidget();
    }
    return globalWidget.init(config);
  },
  /**
   * Destroy the widget instance
   * @param containerId Optional container ID (uses default if not provided)
   */
  destroy: (containerId?: string) => globalWidget?.destroy(containerId),
  /**
   * Update widget configuration
   * @param updates Partial configuration to update
   * @param containerId Optional container ID (uses default if not provided)
   */
  updateConfig: (updates: Partial<CuadraUIKitConfig>, containerId?: string) =>
    globalWidget?.updateConfig(updates, containerId),
  /**
   * Get widget instance
   * @param containerId Optional container ID (uses default if not provided)
   * @returns Widget instance or null
   */
  getInstance: (containerId?: string) => globalWidget?.getInstance(containerId),
  /**
   * Force refetch models (only works in selector mode)
   */
  refetchModels: () => {
    if (typeof window !== "undefined") {
      const refetch = (
        window as Window & { __cuadraUIKitRefetchModels?: () => Promise<void> }
      ).__cuadraUIKitRefetchModels;
      if (refetch) {
        refetch();
      }
    }
  },
  /**
   * Widget version
   */
  version: "0.1.0",
};

// Set on window directly for UMD/script tag usage
// The UMD build will expose this, but we also set it here for compatibility
if (typeof window !== "undefined") {
  (window as { CuadraUIKit?: typeof CuadraUIKitAPI }).CuadraUIKit =
    CuadraUIKitAPI;

  // Auto-initialize from script tag attributes (if script tag exists)
  const initFromScriptTag = () => {
    // Find script tag that loaded this file
    const scripts = document.querySelectorAll('script[src*="cuadra-uikit"]');
    const script = Array.from(scripts).find((s) => {
      const src = s.getAttribute("src") || "";
      return src.includes("cuadra-uikit.umd.js");
    }) as HTMLScriptElement | undefined;

    if (script && script.dataset.autoInit !== "false") {
      autoInitFromAttributes(script);
    }
  };

  // Wait for DOM to be ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFromScriptTag);
  } else {
    setTimeout(initFromScriptTag, 0);
  }
}

function autoInitFromAttributes(script: HTMLScriptElement): void {
  if (!script || !globalWidget) return;

  // Parse config from script tag
  const config = CuadraUIKitWidget.parseConfigFromAttributes(script);

  // Find container
  const containerId = config.containerId || "cuadra-chat";
  const container = document.getElementById(containerId);

  // Parse container attributes
  if (container) {
    const containerConfig = CuadraUIKitWidget.parseConfigFromAttributes(
      script,
      container,
    );
    Object.assign(config, containerConfig);
  }

  // Initialize if baseUrl or proxyUrl is provided
  if (config.baseUrl || config.proxyUrl) {
    globalWidget.init(config as CuadraUIKitConfig);
  }
}

// Export the API object as default - UMD wrapper will expose this as window.CuadraUIKit
export default CuadraUIKitAPI;
